import React from 'react'
import { InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Badge, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { Link } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useCustomerDetailContext } from "contexts/CustomerDetailContext"
import { useAuth, useTablePagination } from "hooks";

import { useLazyListCustomerShipmentsQuery, } from "store/services/customerApi";

const CustomerShipmenItems = ({ setTotalShipments }) => {

    const { customerDetail } = useCustomerDetailContext();

    const { isVendor, isCustomer } = useAuth();

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const columns = [
        {
            name: "ID",
            selector: (row) => row?.shipmentId,
            cell: (row) => row?.shipmentId,
            width: "150px",
        },

        {
            name: "Sender",
            selector: (row) =>
                row?.customerUser?.name || row?.vendor?.shopName || row?.vendor?.ownerName || "N/A",
            cell: (row) => (
                <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row?.customerUser?.name || row?.vendor?.shopName || row?.vendor?.ownerName || "N/A"}
                </Box>
            ),
            omit: isVendor,
        },

        {
            name: "Receiver",
            selector: (row) => row?.receiver?.customerUser?.name,
            cell: (row) => (
                <div>
                    <span className="text-sm">{row?.receiver?.customerUser?.name}</span>
                    <br />
                    <span className="text-sm  text-gray-500 dark:text-gray-300">{row?.receiver?.phone}</span>
                </div>
            ),

        },
        {
            name: "From",
            selector: (row) => row?.sourceBranch?.name || "Loading...",
            cell: (row) => (
                <span>{row?.sourceBranch?.name || <CircularProgress size={12} />}</span>
            ),
        },
        {
            name: "To",
            selector: (row) => row?.destinationBranch?.name || "Loading...",
            cell: (row) => (
                <span>{row?.destinationBranch?.name || <CircularProgress size={12} />}</span>
            ),
        },
        {
            name: "Pickup Time",
            selector: (row) => <AppTableDateTime date={row?.shipmentPickupTime} />,
        },
        {
            name: "Status",
            selector: (row) => row.status,
            center: true,
            cell: (row) => {
                const statusColors = {
                    Pending: "bg-yellow-700 text-white",
                    Collected: "bg-gray-500 text-white",
                    Loaded: "bg-gray-500 text-white",
                    Delivered: "bg-gray-500 text-white",
                };
                return (
                    <Badge className={`px-1 py-1 rounded-md ${statusColors[row.status] || "bg-gray-500"}`}>
                        {row.status}
                    </Badge>
                );
            },
        },
        {
            name: "Actions",
            cell: (row) => {
                const basePath = isVendor
                    ? "/app/vendor/shipment"
                    : isCustomer
                        ? "/app/customer/shipment"
                        : "/app/crm/shipment";
                const isEditDisabled = (isVendor || isCustomer) && row?.status !== "Pending";

                return (
                    <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                        <>
                            <Link to={`${basePath}/${row?._id}/detail`}>
                                <IconButton size="small" aria-label="View Shipment" color="primary">
                                    <InfoOutlined />
                                </IconButton>
                            </Link>
                            <Link
                                to={isEditDisabled ? "#" : `${basePath}/${row?._id}/edit`}
                                style={{ pointerEvents: isEditDisabled ? "none" : "auto" }}
                            >
                                <IconButton
                                    size="small"
                                    aria-label="Edit Shipment"
                                    disabled={isEditDisabled}
                                    sx={{ opacity: isEditDisabled ? 0.5 : 1 }}
                                >
                                    <ModeEditOutlineTwoTone />
                                </IconButton>
                            </Link>
                        </>
                    </Box>
                );
            },
            ignoreRowClick: true,
            right: true,
        }
    ];

    const [fetchesShipments, { data: shipmentRes, isLoading, isFetching }] =
        useLazyListCustomerShipmentsQuery();

    // Fetch fresh data whenever this tab mounts (tab switch) or pagination changes
    React.useEffect(() => {
        if (!customerDetail?._id) return;

        fetchesShipments({
            customerId: customerDetail?._id,
            page: currentPage,
            limit: perPage,
        });
    }, [currentPage, customerDetail?._id, fetchesShipments, perPage])

    // Keep the tab badge count in sync with the latest total
    React.useEffect(() => {
        if (setTotalShipments && shipmentRes?.totalItems !== undefined) {
            setTotalShipments(shipmentRes.totalItems);
        }
    }, [shipmentRes?.totalItems, setTotalShipments])

    return (
        <>
            <BreadcrumbLayout.Paper>
                {shipmentRes ? (
                    <AppTable
                        columns={columns}
                        data={shipmentRes?.data || []}
                        progressPending={isLoading || isFetching}
                        paginationServer
                        paginationPerPage={perPage}
                        paginationTotalRows={shipmentRes?.totalItems || 0}
                        paginationDefaultPage={currentPage}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                    />
                ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" marginLeft={1}>Loading Shipment...</Typography>
                    </Box>
                )}
            </BreadcrumbLayout.Paper>
        </>
    )
}

export default CustomerShipmenItems